import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import { cn } from '../utils/cn';
import { safeHttpUrl } from '../utils/safeUrl';

// Public page: a customer's invoices, statement and online payment, opened
// from the private link their supplier sent. No login; the token is the key.
const BASE = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export default function CustomerPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState('');
  const [showPaid, setShowPaid] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/customer-portal/${encodeURIComponent(token)}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'This link is not valid');
      setData(body.portal);
    } catch (e) { setError(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const pay = async (id) => {
    setPaying(id);
    try {
      const res = await fetch(`${BASE}/customer-portal/${encodeURIComponent(token)}/invoices/${id}/pay`, { method: 'POST' });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message);
      const target = safeHttpUrl(body.url);
      if (!target) throw new Error('Payment could not be started');
      window.location.assign(target);
    } catch (e) { setError(e.message || 'Payment could not be started'); setPaying(''); }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F4F6F9] flex items-center justify-center p-4">
        {error ? <p className="text-[15px] text-[#3E3E3C] bg-white border border-[#E5E5E5] rounded-lg p-6 max-w-md text-center shadow-[0_1px_3px_rgba(0,0,0,0.08)]">{error}</p>
          : <Loader2 className="animate-spin text-[#706E6B]" />}
      </div>
    );
  }
  const open = data.invoices.filter((i) => i.canPay);
  const rest = data.invoices.filter((i) => !i.canPay);
  const Row = (i) => (
    <tr key={i.id} className="border-t border-[#F3F2F2]">
      <td className="px-3 py-3 font-semibold">{i.invoiceNumber}<span className="block text-[11px] font-normal text-[#706E6B]">{fmtDate(i.issueDate)}</span></td>
      <td className={cn('px-3 py-3', i.overdue && 'text-[#BA0517] font-semibold')}>{fmtDate(i.dueDate)}{i.overdue && <span className="block text-[11px]">Overdue</span>}</td>
      <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(i.total, i.currency)}</td>
      <td className="px-3 py-3 text-right tabular-nums font-semibold">{i.balance > 0 ? formatCurrency(i.balance, i.currency) : <span className="text-[#2E844A] font-normal capitalize">{i.status}</span>}</td>
      <td className="px-3 py-3 text-right">{i.canPay && (
        <button type="button" disabled={!!paying} onClick={() => pay(i.id)} className="h-8 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold disabled:opacity-60">
          {paying === i.id ? 'Opening…' : 'Pay'}
        </button>
      )}</td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F9] text-[#181818]">
      <div className="max-w-3xl mx-auto px-4 py-8 flex flex-col gap-5">
        <header className="flex flex-col gap-1">
          <p className="text-[12px] uppercase text-[#706E6B]">{data.company.name}</p>
          <h1 className="text-[22px] font-semibold">Invoices for {data.customer.name}</h1>
          <p className="text-[13px] text-[#706E6B]">
            {Object.keys(data.outstanding).length
              ? <>Amount due: <strong className="text-[#181818]">{Object.entries(data.outstanding).map(([c, v]) => formatCurrency(v, c)).join(' + ')}</strong></>
              : 'Nothing is due. Thank you!'}
          </p>
        </header>
        {error && <p className="text-[13px] px-3 py-2 rounded border bg-[#FEF1EE] border-[#F8B6A8] text-[#8C1B0F]">{error}</p>}
        <section className="bg-white border border-[#E5E5E5] rounded-lg overflow-x-auto shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <table className="w-full text-[13px] min-w-[520px]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr><th className="px-3 py-2 text-left">Invoice</th><th className="px-3 py-2 text-left">Due</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {open.map(Row)}
              {showPaid && rest.map(Row)}
            </tbody>
          </table>
          {!open.length && !showPaid && <p className="text-[13px] text-[#706E6B] text-center py-6">No unpaid invoices.</p>}
        </section>
        <div className="flex flex-wrap gap-3 items-center text-[13px]">
          {rest.length > 0 && <button type="button" className="text-[#0176D3] hover:underline" onClick={() => setShowPaid(!showPaid)}>{showPaid ? 'Hide paid invoices' : `Show ${rest.length} paid invoice(s) from the last year`}</button>}
          <a className="text-[#0176D3] hover:underline flex items-center gap-1" href={`${BASE}/customer-portal/${encodeURIComponent(token)}/statement.pdf`}><Download size={14} />Download statement (PDF)</a>
        </div>
        <footer className="text-[12px] text-[#706E6B] border-t border-[#D8DDE6] pt-4">
          Questions about an invoice? Contact {data.company.name}{data.company.email ? ` at ${data.company.email}` : ''}{data.company.phone ? ` or ${data.company.phone}` : ''}.
          This private link works until {fmtDate(data.expiresAt)}.
        </footer>
      </div>
    </div>
  );
}
